import { Component, Input, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Store } from '../../../core/models';
import { BannerAnalysisDataService } from '../../../core/services/banner-analysis-data.service';
import { DemographicDataService } from '../../../core/services/demographic-data.service';
import { Subscription } from 'rxjs';
import { AdditionalInsightsDataService } from 'src/app/core/services/additional-insights-data.service';
import { HighlightsDataService } from '../../../core/services/highlights-data.service';
import { StoreHighlight } from '../store-critical-insights-panel/store-highlights-panel/store-highlight';

@Component({
  standalone: false,
  selector: 'mtn-store-detail-pdf-second-page',
  templateUrl: './store-detail-pdf-second-page.component.html',
  styleUrls: ['./store-detail-pdf-second-page.component.scss']
})
export class StoreDetailPdfSecondPageComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() store: Store;
  @Input() criticalInsights: any = {};
  @Input() salesPerformance: any = {};
  @Input() additionalInsights: any = {};

  highlights: StoreHighlight[] = [];

  private subscription: Subscription = new Subscription();
  regionText = '7 minutes Drive Time';

  demographicData = {
    ethnicity: {
      asian: 0,
      hispanic: 0,
      other: 0,
      black: 0,
      white: 0
    },
    population: {
      total: 0,
      growth2yr: 0,
      householdGrowth2yr: 0
    },
    people: {
      averageAge: 0,
      educated: 0,
      householdSize: 0
    },
    income: {
      average: 0,
      median: 0
    }
  };
    storeRatings: any= [
    [
      { name: 'Ceiling Condition', rating: '3/5' },
      { name: 'Checkstand Condition', rating: '3/5' }
    ],
    [
      { name: 'Exterior Condition', rating: '4/5' },
      { name: 'Floor Condition', rating: '3/5' }
    ],
    [
      { name: 'Refrigeration Condition', rating: '3/5' },
      { name: 'Shelving Condition', rating: '3/5' }
    ],
    [
      { name: 'Visibility from Road', rating: '3/5' },
      { name: 'Wall Condition', rating: '3/5' }
    ]
  ];

  parkingLotRatings: any = [
    [
      { name: 'General Condition', rating: 'NA' },
      { name: 'Ingress/Egress', rating: '5/5' }
    ],
    [
      { name: 'Lighting', rating: '5/5' },
      { name: 'Traffic Flow', rating: '5/5' }
    ]
  ];

  cotenantRatings: any = [
    [
      { name: 'Exterior Condition', rating: '5/5' },
      { name: 'Synergy', rating: '5/5' }
    ]
  ];

 additionalInfo = {
    servicesCount: 0,
    services: 'Not Available',
    analystRatingDate: 'Not Available'
  };
  constructor(private demographicDataService: DemographicDataService,
              private additionalInsightsDataService: AdditionalInsightsDataService,
              private bannerAnalysisDataService: BannerAnalysisDataService,
              private highlightsDataService: HighlightsDataService) {}

  ngOnInit(): void {
    this.subscription.add(
      this.demographicDataService.demographicData$.subscribe((data: any) => {
        if (data.demographics) {
          this.updateDemographicData(data);
        }
      })
    );

    this.subscription.add(
      this.bannerAnalysisDataService.bannerAnalysis$.subscribe((data: any) => {
        this.updateBannerAnalysisData(data);
      })
    );
     this.subscription.add(
        this.additionalInsightsDataService.additionalInsights$.subscribe((data: any) => {
        this.updateAdditionalInsights(data);
      })
    );

    this.subscription.add(
      this.highlightsDataService.highlights$.subscribe((highlights: StoreHighlight[]) => {
        this.highlights = highlights;
      })
    );
  }

  ngAfterViewInit(): void {
    // Render charts after view initialization
    setTimeout(() => this.renderBannerCharts(), 300);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateDemographicData(data: any): void {
    const demographics = data.demographics;

    // Update region text
    if (data.regionType === 'DRIVE_TIME') {
      this.regionText = `${data.regionValue} Drive Time`;
    } else if (data.regionType === 'RING') {
      this.regionText = `${data.regionValue} Ring`;
    } else {
      this.regionText = 'Market Area';
    }

    // Update demographic data
    this.demographicData = {
      ethnicity: {
        asian: this.round(demographics.ethnicityAsian * 100, 1),
        hispanic: this.round(demographics.ethnicityHispanic * 100, 1),
        other: this.round(demographics.ethnicityOther * 100, 1),
        black: this.round(demographics.ethnicityBlack * 100, 1),
        white: this.round(demographics.ethnicityWhite * 100, 1)
      },
      population: {
        total: demographics.populationEstimate,
        growth2yr: this.round(demographics.populationHistoricalGrowth2Year * 100, 2),
        householdGrowth2yr: this.round(demographics.populationProjectedGrowth5Year * 100, 2)
      },
      people: {
        averageAge: demographics.ageAverage,
        educated: this.round(demographics.educationCollegeOrDegree * 100, 1),
        householdSize: demographics.householdsPersonsPer
      },
      income: {
        average: demographics.householdsAverageIncome,
        median: demographics.householdsMedianIncome
      }
    };
  }

  formatNum(value: number): string {
    if (value == null || isNaN(value)) return '0';
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  }

  formatNumDecimal(value: number): string {
    if (value == null || isNaN(value)) return '0.0';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
  }

  private round(value: number, decimals: number): number {
    return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  private updateBannerAnalysisData(data: any): void {
    if (data.bannerName) {
      this.bannerAnalysis.banner = data.bannerName;
    }
    if (data.marketName) {
      this.bannerAnalysis.market = `${data.marketName} CBSA`;
    }
    if (data.salesSqftData) {
      this.bannerAnalysis.salesSqft = data.salesSqftData;
    }
    if (data.salesVolumeData) {
      this.bannerAnalysis.salesVolume = data.salesVolumeData;
    }
    if (data.storeCountData) {
      this.bannerAnalysis.storeCount = data.storeCountData;
    }

    // Re-render charts when data updates
    setTimeout(() => this.renderBannerCharts(), 200);
  }
 private updateAdditionalInsights(data: any): void {
    // Update services info
    if (data.services?.surveyDate) {
      this.additionalInfo.servicesCount = data.services.count();
      this.additionalInfo.services = this.getAvailableServices(data.services);
    }

    // Update ratings info
    if (data.ratings?.hasMinimumRequiredRatingCount()) {
      const date = new Date(data.ratings.ratingDate);
      this.additionalInfo.analystRatingDate = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      this.updateRatingsData(data.ratings);
    }
  }
    private updateRatingsData(ratings: any): void {
    // Update store ratings using the same method as the wizard
    this.storeRatings = [
      [
        { name: 'Ceiling Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_CEILING_CONDITION')?.value) },
        { name: 'Checkstand Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_CHECKSTAND_CONDITION')?.value) }
      ],
      [
        { name: 'Exterior Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_EXTERIOR_CONDITION')?.value) },
        { name: 'Floor Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_FLOOR_CONDITION')?.value) }
      ],
      [
        { name: 'Refrigeration Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_REFRIGERATION_CONDITION')?.value) },
        { name: 'Shelving Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_SHELVING_CONDITION')?.value) }
      ],
      [
        { name: 'Visibility from Road', rating: this.formatRating(ratings.getRatingOfType('STORE_VISIBILITY')?.value) },
        { name: 'Wall Condition', rating: this.formatRating(ratings.getRatingOfType('STORE_WALL_CONDITION')?.value) }
      ]
    ];

    // Update parking lot ratings (only parking lot data)
    this.parkingLotRatings = [
      [
        { name: 'General Condition', rating: this.formatRating(ratings.getRatingOfType('PARKING_LOT_CONDITION')?.value) },
        { name: 'Ingress/Egress', rating: this.formatRating(ratings.getRatingOfType('PARKING_LOT_INGRESS_EGRESS')?.value) }
      ],
      [
        { name: 'Lighting', rating: this.formatRating(ratings.getRatingOfType('PARKING_LOT_LIGHTING')?.value) },
        { name: 'Traffic Flow', rating: this.formatRating(ratings.getRatingOfType('PARKING_LOT_FLOW')?.value) }
      ]
    ];

    // Update cotenant ratings (separate array)
    this.cotenantRatings = [
      [
        { name: 'Exterior Condition', rating: this.formatRating(ratings.getRatingOfType('COTENANT_EXTERIOR_CONDITION')?.value) },
        { name: 'Synergy', rating: this.formatRating(ratings.getRatingOfType('COTENANT_SYNERGY')?.value) }
      ]
    ];
  }

   private getAvailableServices(services: any): string {
    const availableServices = [];
    if (services.bakery) availableServices.push('Bakery');
    if (services.deli) availableServices.push('Deli');
    if (services.meat) availableServices.push('Meat');
    if (services.seafood) availableServices.push('Seafood');
    if (services.liquor) availableServices.push('Liquor');
    if (services.beer) availableServices.push('Beer');
    if (services.wine) availableServices.push('Wine');
    if (services.pharmacy) availableServices.push('Pharmacy');
    if (services.floral) availableServices.push('Floral');
    if (services.cheese) availableServices.push('Cheese');
    return availableServices.length > 0 ? availableServices.join(', ') : 'Not Available';
  }

  private renderBannerCharts(): void {
    const canvases = document.querySelectorAll('.banner-mini-chart');
    canvases.forEach((canvas: HTMLCanvasElement) => {
      const percentile = parseInt(canvas.getAttribute('data-percentile') || '0');
      const color = canvas.getAttribute('data-color') || '#ddd';
      this.drawMiniChart(canvas, percentile, color);
    });
  }
 private formatRating(value: number): string {
    return value ? `${value}/5` : 'NA';
  }
  private drawMiniChart(canvas: HTMLCanvasElement, percentile: number, color: string): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 12;
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + (2 * Math.PI * Math.max(percentile, 1) / 100);

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background circle
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw progress arc
    if (percentile > 0 || color) {
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      ctx.strokeStyle = color || '#ff0000';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  bannerAnalysis = {
    banner: 'WALMART',
    market: 'Los Angeles-Long Beach-Anaheim, CA CBSA',
    salesSqft: null,
    salesVolume: null,
    storeCount: null
  };

  shouldHideBannerAnalysisInSecondPage(): boolean {
    return this.highlights.length > 10 || this.highlights.length < 5;
  }

}
